import { useEffect, useState } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { LocaleProvider } from "@/lib/i18n/context";
import { ThemeProvider } from "@/lib/theme";
import { DEFAULT_LOCALE, dirFor, isLocale, type Locale } from "@/lib/i18n/config";
import { AuthLayout } from "@/layouts/AuthLayout";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { CandidateLayout } from "@/layouts/CandidateLayout";
import { CareersShell } from "@/layouts/CareersShell";
import { HomePage } from "@/pages/HomePage";
import { SignInPage } from "@/pages/SignInPage";
import { SignUpPage } from "@/pages/SignUpPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { JobsPage } from "@/pages/JobsPage";
import { NewJobPage } from "@/pages/NewJobPage";
import { JobDetailPage } from "@/pages/JobDetailPage";
import { CandidatesPage } from "@/pages/CandidatesPage";
import { NewCandidatePage } from "@/pages/NewCandidatePage";
import { InterviewsPage } from "@/pages/InterviewsPage";
import { InterviewDetailPage } from "@/pages/InterviewDetailPage";
import { CandidateInterviewPage } from "@/pages/CandidateInterviewPage";
import { PartnersPage } from "@/pages/PartnersPage";
import { PartnershipAcceptPage } from "@/pages/PartnershipAcceptPage";
import { CareersPage } from "@/pages/CareersPage";
import { CareerDetailPage } from "@/pages/CareerDetailPage";
import { CandidateInterviewsPage } from "@/pages/CandidateInterviewsPage";
import { ContactPage } from "@/pages/ContactPage";
import { ReportsPage } from "@/pages/ReportsPage";
import { CandidateProfilePage } from "@/pages/CandidateProfilePage";
import { TalentPage } from "@/pages/TalentPage";
import { TalentDetailPage } from "@/pages/TalentDetailPage";
import { PageViewTracker } from "@/components/analytics/page-view-tracker";
import { isReportsAdmin } from "@/lib/reports-admin";
import type { User } from "@/lib/types";

function readLocaleCookie(): Locale {
  const match = document.cookie.match(/(?:^|; )locale=([^;]+)/);
  const value = match ? decodeURIComponent(match[1]) : "";
  return isLocale(value) ? value : DEFAULT_LOCALE;
}

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center text-muted">
      Loading…
    </div>
  );
}

function ManagerRoute({
  user,
  onSignOut,
}: {
  user: User | null | undefined;
  onSignOut: () => void;
}) {
  const location = useLocation();
  if (user === undefined) return <LoadingScreen />;
  if (!user) {
    return (
      <Navigate
        to={`/sign-in?redirect=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }
  if (user.role !== "manager") {
    return <Navigate to="/careers" replace />;
  }
  return (
    <DashboardLayout user={user} onSignOut={onSignOut}>
      <Outlet />
    </DashboardLayout>
  );
}

function CandidateRoute({
  user,
  onSignOut,
}: {
  user: User | null | undefined;
  onSignOut: () => void;
}) {
  const location = useLocation();
  if (user === undefined) return <LoadingScreen />;
  if (!user) {
    return (
      <Navigate
        to={`/sign-in?redirect=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }
  if (user.role !== "candidate") {
    return <Navigate to="/dashboard" replace />;
  }
  return (
    <CandidateLayout user={user} onSignOut={onSignOut}>
      <Outlet />
    </CandidateLayout>
  );
}

function ReportsRoute({ user }: { user: User | null | undefined }) {
  if (user === undefined) return <LoadingScreen />;
  if (!user || !isReportsAdmin(user.email)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <ReportsPage currentUserId={user.id} />;
}

export default function App() {
  const [locale, setLocale] = useState<Locale>(readLocaleCookie);
  const [user, setUser] = useState<User | null | undefined>(undefined);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dirFor(locale);
  }, [locale]);

  useEffect(() => {
    fetch("/api/auth/me", { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) {
          setUser(null);
          return;
        }
        const data = (await res.json()) as { user: User };
        setUser(data.user);
      })
      .catch(() => setUser(null));
  }, []);

  const refreshUser = async () => {
    try {
      const res = await fetch("/api/auth/me", { credentials: "include" });
      if (!res.ok) {
        setUser(null);
        return;
      }
      const data = (await res.json()) as { user: User };
      setUser(data.user);
    } catch {
      setUser(null);
    }
  };

  const onLocaleChange = async (next: Locale) => {
    await fetch("/api/locale", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ locale: next }),
    });
    setLocale(next);
  };

  return (
    <ThemeProvider>
    <LocaleProvider locale={locale} onLocaleChange={onLocaleChange}>
      <PageViewTracker />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route element={<AuthLayout />}>
          <Route
            path="/sign-in"
            element={<SignInPage onAuth={refreshUser} />}
          />
          <Route
            path="/sign-up"
            element={<SignUpPage onAuth={refreshUser} />}
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>

        <Route
          element={
            <ManagerRoute user={user} onSignOut={() => setUser(null)} />
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/jobs/new" element={<NewJobPage />} />
          <Route path="/jobs/:id" element={<JobDetailPage />} />
          <Route path="/candidates" element={<CandidatesPage />} />
          <Route path="/candidates/new" element={<NewCandidatePage />} />
          <Route path="/interviews" element={<InterviewsPage />} />
          <Route path="/interviews/:id" element={<InterviewDetailPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/talent" element={<TalentPage />} />
          <Route path="/talent/:id" element={<TalentDetailPage />} />
          <Route path="/reports" element={<ReportsRoute user={user} />} />
        </Route>

        <Route
          element={
            <CandidateRoute user={user} onSignOut={() => setUser(null)} />
          }
        >
          <Route
            path="/candidate/interviews"
            element={<CandidateInterviewsPage />}
          />
          <Route
            path="/candidate/profile"
            element={<CandidateProfilePage />}
          />
        </Route>

        <Route
          element={
            <CareersShell user={user} onSignOut={() => setUser(null)} />
          }
        >
          <Route path="/careers" element={<CareersPage />} />
          <Route
            path="/careers/:id"
            element={<CareerDetailPage user={user} />}
          />
          <Route path="/contact" element={<ContactPage />} />
        </Route>

        <Route
          path="/partnerships/accept/:token"
          element={<PartnershipAcceptPage onAuth={refreshUser} />}
        />
        <Route path="/interview/:token" element={<CandidateInterviewPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </LocaleProvider>
    </ThemeProvider>
  );
}
